import type { GlobalSwatchesData } from '@/types/global-data';
import { cloneDeepObject } from '@/utils/common';
import type { Component, GroupTypeSetting } from '../../common/utils/types';
import useEmitToFrame from '@/modules/editor/modules/preview/hooks/useEmitToFrame';

export function waitForElement(doc: Document, selector: string, cb?: () => void) {
  const element = doc?.querySelector(selector);
  if (element) {
    cb?.();
  } else {
    //hotfix contentDocument not reactive (need to refactor all waitForElement)
    const $iframe = document.querySelector<HTMLIFrameElement>('.iframe');
    doc = $iframe?.contentDocument || ($iframe?.contentWindow as any).document;
    setTimeout(() => waitForElement(doc, selector, cb), 500);
  }
}

/**
 * When need emit data to iframe?
 * - First load PageSection
 * - Add new Section
 * - Section change position
 * - Delete a Section
 * - Hide/Show a Section
 * - Add new Component to Section
 * - Component change setting
 * - Move a Component
 * - Remove a Component
 * - Undo/Redo
 */

export function emitInitBuilder(data: Component[], callback?: () => void, className = '.iframe') {
  const $iframe = document.querySelector<HTMLIFrameElement>(className);
  if ($iframe) {
    // const $doc = $iframe.contentDocument || ($iframe.contentWindow && ($iframe.contentWindow as any).document);
    const $win = $iframe.contentWindow;
    // const $body = $doc.querySelector('body');
    // Create a new event, allow bubbling, and provide any data you want to pass to the "detail" property
    const event = new CustomEvent('init-builder', {
      bubbles: true,
      detail: {
        data: cloneDeepObject(data),
      },
    });
    if ($win) {
      waitForElement($win.document, '.builder', () => {
        $win.dispatchEvent(event);
        if (callback) {
          callback();
        }
      });
    }
  }
}

export function emitAddEntity(
  {
    entity,
    id,
    position,
  }: {
    entity: Component;
    id?: string;
    position?: number;
  },
  callback?: () => void,
  className = '.iframe',
) {
  const $iframe = document.querySelector<HTMLIFrameElement>(className);
  if ($iframe) {
    // const $doc = $iframe.contentDocument || ($iframe.contentWindow && ($iframe.contentWindow as any).document);
    const $win = $iframe.contentWindow;
    // const $body = $doc.querySelector('body');
    // Create a new event, allow bubbling, and provide any data you want to pass to the "detail" property

    const event = new CustomEvent('add-entity', {
      bubbles: true,
      detail: {
        entity,
        id,
        position,
      },
    });
    if ($win) {
      $win.dispatchEvent(event);
      if (callback) {
        callback();
      }
    }
  }
}

export function emitRemoveEntity(uid: string, callback?: () => void, className = '.iframe') {
  const $iframe = document.querySelector<HTMLIFrameElement>(className);
  if ($iframe) {
    // const $doc = $iframe.contentDocument || ($iframe.contentWindow && ($iframe.contentWindow as any).document);
    const $win = $iframe.contentWindow;
    // Create a new event, allow bubbling, and provide any data you want to pass to the "detail" property
    const event = new CustomEvent('remove-entity', {
      bubbles: true,
      detail: {
        uid: uid,
      },
    });
    if ($win) {
      $win.dispatchEvent(event);
      if (callback) {
        callback();
      }
    }
  }
}

export function emitMoveEntity(
  { uid, to, position }: { uid: string; to: string; position: number },
  callback?: () => void,
  className = '.iframe',
) {
  const $iframe = document.querySelector<HTMLIFrameElement>(className);
  if ($iframe) {
    // const $doc = $iframe.contentDocument || ($iframe.contentWindow && ($iframe.contentWindow as any).document);
    const $win = $iframe.contentWindow;
    // Create a new event, allow bubbling, and provide any data you want to pass to the "detail" property
    const event = new CustomEvent('move-entity', {
      bubbles: true,
      detail: { uid, to, position },
    });
    if ($win) {
      $win.dispatchEvent(event);
      if (callback) {
        callback();
      }
    }
  }
}

export function emitUpdateEntityProp(
  detail: { uid: string; propName: string; propValue: any; group: GroupTypeSetting },
  callback?: () => void,
  className = '.iframe',
) {
  const $iframe = document.querySelector<HTMLIFrameElement>(className);
  if ($iframe) {
    // const $doc = $iframe.contentDocument || ($iframe.contentWindow && ($iframe.contentWindow as any).document);
    const $win = $iframe.contentWindow;
    // const $body = $doc.querySelector('body');
    // Create a new event, allow bubbling, and provide any data you want to pass to the "detail" property
    const event = new CustomEvent('update-entity-prop', {
      bubbles: true,
      detail: cloneDeepObject(detail),
    });
    if ($win) {
      $win.dispatchEvent(event);
      if (callback) {
        callback();
      }
    }
  }
}

export function emitSetGlobalStyle(
  { globalStyle, mobileOnly }: { globalStyle: any; mobileOnly?: boolean },
  callback?: () => void,
  className = '.iframe',
) {
  const $iframe = document.querySelector<HTMLIFrameElement>(className);
  if ($iframe) {
    // const $doc = $iframe.contentDocument || ($iframe.contentWindow && ($iframe.contentWindow as any).document);
    const $win = $iframe.contentWindow;
    // Create a new event, allow bubbling, and provide any data you want to pass to the "detail" property
    const event = new CustomEvent('set-global-style', {
      bubbles: true,
      detail: { data: cloneDeepObject(globalStyle), mobileOnly },
    });
    if ($win) {
      waitForElement($win.document, '.builder', () => {
        $win.dispatchEvent(event);
        if (callback) {
          callback();
        }
      });
    }
  }
}

export function emitUpdateGlobalSwatchesData(
  { swatchesData }: { swatchesData: GlobalSwatchesData[] },
  callback?: () => void,
  className = '.iframe',
) {
  const $iframe = document.querySelector<HTMLIFrameElement>(className);
  if ($iframe) {
    // const $doc = $iframe.contentDocument || ($iframe.contentWindow && ($iframe.contentWindow as any).document);
    const $win = $iframe.contentWindow;
    // Create a new event, allow bubbling, and provide any data you want to pass to the "detail" property
    const event = new CustomEvent('update-global-swatches-data', {
      bubbles: true,
      detail: { data: cloneDeepObject(swatchesData) },
    });
    if ($win) {
      waitForElement($win.document, '.builder', () => {
        $win.dispatchEvent(event);
        if (callback) {
          callback();
        }
      });
    }
  }
}

function emitEventToBuilder(eventName: string, data: any, callback?: () => void) {
  const $iframe = document.querySelector<HTMLIFrameElement>('.iframe');
  if ($iframe) {
    const $win = $iframe.contentWindow;
    const event = new CustomEvent(eventName, {
      bubbles: true,
      detail: typeof data === 'object' ? cloneDeepObject(data) : data,
    });
    if ($win) {
      waitForElement($win.document, '.builder', () => {
        $win.dispatchEvent(event);
        if (callback) {
          callback();
        }
      });
    }
  }
}

export function emitOnOffHeaderFooter(data?: string) {
  const { emitEventToToolbox } = useEmitToFrame();
  emitEventToToolbox('on-off-header-footer', JSON.parse(data || '{}'));
}

export function emitUpdateCollectionInfo(data: { baseID?: string | null }, callback?: () => void) {
  const { emitEventToToolbox } = useEmitToFrame();
  emitEventToToolbox('update-collection-info', data, callback);
}

export function emitUpdateShopInfo(data: { shopHandle: string; shopToken: string }, callback?: () => void) {
  emitEventToBuilder('update-shop-info', data, callback);
}

export function emitRevalidateQueryStorefront(callback?: () => void) {
  emitEventToBuilder('revalidate-query', {}, callback);
}

export function emitSelectModal(modalId?: string, callback?: () => void) {
  emitEventToBuilder(
    'set-active-modal',
    {
      modalId,
    },
    callback,
  );
}
